import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { cloudPrepApi } from '../../../services/cloudPrepApi';
import { startTestApi } from '../../../services/startTestAPI/startTestApi';

type Answer = {
  isActive: boolean;
  answerOption: string;
  isCorrectAnswer: boolean;
  correctAnswer: string;
  answerPlaceHolder: string;
};

type QuestionIdentifier = {
  questionId: string;
  sectionId: string;
  questionIndex: number;
};

export type HightLightText = {
  selectedText: string;
  startIndex: number;
  bgColor: string;
  underline: string;
  contentType: string;
  isNotes: boolean;
  notes: string;
};

export type HightLightTextData = {
  questionId: string;
  hightLightText: HightLightText[];
};

export type StartedTestId = {
  testId: string[];
  userTestId: string;
  testName: string;
  createdDate: string;
};

export type FiltersType = {
  section: {
    readingWriting: boolean;
    math: boolean;
  };
  module: {
    module1: boolean;
    module2: boolean;
  };
  answerType: {
    correct: boolean;
    incorrect: boolean;
    omitted: boolean;
  };
};

export type TestSettingsState = {
  sectionId: string;
  topicIds: string[];
  numberOfQuestions: number;
  timeAllotted: number;
};

export type Section = {
  id: string;
  isActive: boolean;
  sectionName: string;
  similerSectionNames: string[];
  createdDate: string;
  updatedDate: string | null;
  displaySortOrder: number;
  displayOnWebsite: string;
};

export type AllRoundsData = {
  id: string;
  isActive: boolean;
  roundName: string;
  createdDate: string;
  updatedDate: string | null;
  displaySortOrder: number;
  displayOnWebsite: string;
};
export type CompanyData = {
  id: string;
  isActive: boolean;
  companyName: string;
  examName: string;
  companyText: string;
  companyImage: string;
  createdDate: string;
  updatedDate: string | null;
  displaySortOrder: number;
  displayOnWebsite: string;
  testCount: number;
  companyType: 5000 | 5001 | 5002 | 5003 | 5004 | 5005;
};

type FullTestState = {
  expandedSection: any;
  resultById: any;
  testDetailsUnavailable: boolean;
  isTestStarted: boolean;
  currentTime: number;
  count: number;
  testId: string[];
  testName: string;
  userTestId: string;
  userSelectedCompany: CompanyData;
  userSelectedCompanyTest: UserTest;
  overallresult: any;
  sidebarCollapse: any;
  completedTestData: any;
  filteredData: any;
  startTime: Date | null;
  startedTestId: StartedTestId;
  questionId: string;
  filters: FiltersType;
  isFullTestResultScreen: boolean;
  qId: QuestionIdentifier;
  minimisePanel: boolean;
  isTestInstructionsOpen: boolean;
  isResumeTest: boolean;
  isOnBreak: boolean;
  currentSectionIndex: number;
  currentQuestionIndex: number;
  roundName: string;
  currentSectionName: string;
  allRoundsData: AllRoundsData[];
  allSectionsData: Section[];
  subTopic: string;
  showInstructions: boolean;
  instructions: string;
  hasReadRoundInsructions: boolean;
  hasReadFirstSectionInstructions: boolean;
  selectedComanyName: string;
  selectedCompanyId: string;
  currentCodeingQuestionAnswer: string;
  allCodeingQuestions: any;
  selectedCodeingLanguage: string;
  allCompaniesData: CompanyData[];
  userCompanyTestsStatus: UserCompanyTestsResponse;
  testSettingsState: TestSettingsState;
  isResultsScreen: boolean;
  isDriveTest: boolean;
  nextRound: 'written' | 'interview' | 'hr';
};

export type UserTest = {
  id: string;
  isActive: boolean;
  createdDate: string; // ISO date string
  updatedDate: string | null;
  userId: string;
  testType: 'WRITTEN_TEST'; // could extend with other possible values
  testStatus: 'NOT_YET' | 'STARTED' | 'COMPLETED'; // guessed enum values
  companyId: string;
  testSetId: string;
  testName: string;
  userTestId: string;
  startedOn: string;
};

export type UserCompanyTestsResponse = {
  assignedTests: number;
  data: UserTest[];
  totaltests: number;
};

const initialState: FullTestState = {
  isDriveTest: false,
  expandedSection: {},
  isOnBreak: false,
  isTestStarted: false,
  currentTime: 2700,
  completedTestData: {},
  testId: [],
  userTestId: '',
  testName: '',
  userSelectedCompany: {
    id: '',
    isActive: true,
    companyName: '',
    examName: '',
    companyText: '',
    companyImage: '',
    createdDate: '',
    updatedDate: null,
    displaySortOrder: 0,
    displayOnWebsite: '',
    testCount: 0,
    companyType: 5000
  },
  userSelectedCompanyTest: {
    id: '',
    isActive: false,
    createdDate: '', // ISO date string
    updatedDate: '',
    userId: '',
    testType: 'WRITTEN_TEST', // could extend with other possible values
    testStatus: 'NOT_YET', // guessed enum values
    companyId: '',
    testSetId: '',
    testName: '',
    userTestId: '',
    startedOn: ''
  },
  count: 0,
  overallresult: {},
  sidebarCollapse: false,
  startTime: null,
  startedTestId: {
    testId: [],
    userTestId: '',
    testName: '',
    createdDate: ''
  },
  questionId: '',
  // parentStudentId: '',
  filteredData: [],
  filters: {
    section: {
      readingWriting: true,
      math: true
    },
    module: {
      module1: true,
      module2: true
    },
    answerType: {
      correct: true,
      incorrect: true,
      omitted: true
    }
  },
  isFullTestResultScreen: false,
  qId: {
    questionId: '',
    sectionId: '',
    questionIndex: 0
  },
  minimisePanel: false,
  isTestInstructionsOpen: false,
  isResumeTest: false,
  currentSectionIndex: 0,
  currentQuestionIndex: 0,
  roundName: '',
  currentSectionName: '',
  allRoundsData: [],
  allSectionsData: [],
  subTopic: '',
  showInstructions: true,
  instructions: '',
  hasReadRoundInsructions: false,
  hasReadFirstSectionInstructions: false,
  selectedComanyName: '',
  resultById: {},
  testDetailsUnavailable: false,
  currentCodeingQuestionAnswer: '',
  allCodeingQuestions: [],
  selectedCodeingLanguage: 'java',
  selectedCompanyId: '',
  allCompaniesData: [
    {
      id: '',
      isActive: true,
      companyName: '',
      examName: '',
      companyText: '',
      companyImage: '',
      createdDate: '',
      updatedDate: null,
      displaySortOrder: 0,
      displayOnWebsite: '',
      testCount: 0,
      companyType: 5000
    }
  ],
  userCompanyTestsStatus: {
    assignedTests: 0,
    data: [],
    totaltests: 0
  },
  testSettingsState: {
    sectionId: '',
    topicIds: [],
    numberOfQuestions: 5,
    timeAllotted: 1200
  },
  isResultsScreen: false,
  nextRound: 'written'
};

const startFullTest = createSlice({
  name: 'startFullTest',
  initialState,
  reducers: {
    setResetFilters: state => {
      state.filters = {
        section: {
          readingWriting: true,
          math: true
        },
        module: {
          module1: true,
          module2: true
        },
        answerType: {
          correct: true,
          incorrect: true,
          omitted: true
        }
      };
    },
    setTestSettings: (
      state,
      action: PayloadAction<Partial<TestSettingsState>>
    ) => {
      state.testSettingsState = {
        ...state.testSettingsState,
        ...action.payload
      };
    },
    setIsDriveTest: (state, action) => {
      state.isDriveTest = action.payload;
    },
    setIsResultsScreen: (state, action) => {
      state.isResultsScreen = action.payload;
    },
    setFilteredData: (state, action) => {
      state.filteredData = action.payload;
    },
    setIsTestStarted: (state, action) => {
      state.isTestStarted = action.payload;
    },
    setMinimisePanel: (state, action) => {
      state.minimisePanel = action.payload;
    },
    setIsResumeTest: (state, action) => {
      state.isResumeTest = action.payload;
    },
    setIsFullTestResultScreen: (state, action) => {
      state.isFullTestResultScreen = action.payload;
    },
    setQuestionId: (state, action) => {
      state.questionId = action.payload;
    },
    setQuestionStartTime: (state, action) => {
      state.startTime = action.payload;
    },
    setIsSidebarCollapse: (state, action) => {
      state.sidebarCollapse = action.payload;
    },
    setIsOnBreak: (state, action) => {
      state.isOnBreak = action.payload;
    },
    setTestId: (state, action) => {
      state.testId = action.payload;
    },
    setTestName: (state, action) => {
      state.testName = action.payload;
    },
    setUserTestId: (state, action) => {
      state.userTestId = action.payload;
    },
    setCount: (state, action) => {
      if (action.payload === 'Increment') {
        state.count++;
      } else if (action.payload === 'decrement') {
        state.count--;
      } else if (action.payload) {
        state.count = action.payload;
      } else {
        state.count = 0;
      }
    },
    setCurrentTime: (state, action) => {
      state.currentTime = action.payload;
    },
    setNextRound: (state, action) => {
      state.nextRound = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    setOverallResultSlice: (state, action) => {
      state.overallresult = action.payload;
    },
    setQid: (state, action) => {
      state.qId = action.payload;
    },
    setExpandedSection: (state, action) => {
      state.expandedSection = action.payload;
    },
    setStartedTestId: (state, action) => {
      const { testId, testName, createdDate, userTestId } = action.payload;
      state.startedTestId.testId = testId;
      state.startedTestId.testName = testName;
      state.startedTestId.userTestId = userTestId;
      state.startedTestId.createdDate = createdDate;
    },
    setIsTestInstructionsOpen: (state, action) => {
      state.isTestInstructionsOpen = action.payload;
    },
    setCurrentSectionIndex: (state, action) => {
      state.currentSectionIndex = action.payload;
    },
    setCurrentQuestionIndex: (state, action) => {
      state.currentQuestionIndex = action.payload;
    },
    setRoundName: (state, action) => {
      state.roundName = action.payload;
    },
    setAllRoundsData: (state, action) => {
      state.allRoundsData = action.payload;
    },
    setAllSectionsData: (state, action) => {
      state.allSectionsData = action.payload;
    },
    setCurrentSectionName: (state, action) => {
      state.currentSectionName = action.payload;
    },
    setSubTopicName: (state, action) => {
      state.subTopic = action.payload;
    },
    setShowInstructions: (state, action) => {
      state.showInstructions = action.payload;
    },
    setInstructions: (state, action) => {
      state.instructions = action.payload;
    },
    setHasReadRoundInsructions: (state, action) => {
      state.hasReadRoundInsructions = action.payload;
    },
    setHasReadFirstSectionInstructions: (state, action) => {
      state.hasReadFirstSectionInstructions = action.payload;
    },
    setSelectedCompanyName: (state, action) => {
      state.selectedComanyName = action.payload;
    },
    setCurrentCodeingQuestionAnswer: (state, action) => {
      state.currentCodeingQuestionAnswer = action.payload;
    },
    setAllCodeingQuestions: (state, action) => {
      state.allCodeingQuestions = action.payload;
    },
    setSelectedCodeingLanguage: (state, action) => {
      state.selectedCodeingLanguage = action.payload;
    },
    setAllCompaniesData: (state, action) => {
      state.allCompaniesData = action.payload;
    },
    setSelectedCompanyId: (state, action) => {
      state.selectedCompanyId = action.payload;
    },
    setUserCompanyTestsStatus: (state, action) => {
      state.userCompanyTestsStatus = action.payload;
    },
    setUserSelectedCompany: (state, action) => {
      state.userSelectedCompany = action.payload;
    },
    setUserSelectedCompanyTest: (state, action) => {
      state.userSelectedCompanyTest = action.payload;
    },
    setResultByIdTestDTO: (state, action) => {
      state.resultById = { ...state.resultById, testDTO: action.payload };
    },
    setTestDetailsUnavailable: (state, action) => {
      state.testDetailsUnavailable = action.payload;
    },
    setInitailStartFullTestState: (state, action) => {
      return action.payload;
    },
    setResetSatrtFullTest: () => {
      return initialState;
    },
    resetTestSettings: state => {
      state.testSettingsState = initialState.testSettingsState;
    }
  },
  extraReducers: builder => {
    builder.addMatcher(
      startTestApi.endpoints.getTestResults.matchFulfilled,
      (state, action) => {
        state.overallresult = action.payload.data;
      }
    );
    builder.addMatcher(
      startTestApi.endpoints.getTestResultById.matchFulfilled,
      (state, action) => {
        const incoming = action.payload.data;
        const existingTestDTO = state.resultById?.testDTO;
        const isSameTest = state.resultById?.id === incoming?.id;

        state.resultById = {
          ...incoming,
          ...(isSameTest && existingTestDTO && !incoming?.testDTO
            ? { testDTO: existingTestDTO }
            : {})
        };
      }
    );
    builder.addMatcher(
      startTestApi.endpoints.getDriveTableResults.matchFulfilled,
      (state, action) => {
        state.overallresult = action.payload.data;
      }
    );
    builder.addMatcher(
      startTestApi.endpoints.getDriveResult.matchFulfilled,
      (state, action) => {
        const paylaload = {
          ...action.payload.data,
          testDTO: { ...action.payload.data.writtenTest }
        };
        state.resultById = paylaload;
      }
    );
    builder.addMatcher(
      startTestApi.endpoints.fetchMockTestResuls.matchFulfilled,
      (state, action) => {
        const paylaload = {
          ...action.payload.data,
          testDTO: { ...action.payload.data.mockTestDTO }
        };
        state.resultById = paylaload;
      }
    );
    builder.addMatcher(
      cloudPrepApi.endpoints.fetchCloudPrepUserTest.matchFulfilled,
      (state, action) => {
        const rawResult = action.payload.data;

        const qFindCorrectOption = (questions: any[], questionId: string) => {
          const question = questions.find(q => q.id === questionId);
          return question?.options?.find((opt: any) => opt.isCorrectAnswer)
            ?.answerOption;
        };
        const qFindSelectedText = (
          questions: any[],
          questionId: string,
          selectedLabel: string
        ) => {
          const question = questions.find(q => q.id === questionId);
          const option = question?.options?.find(
            (opt: any) => opt.answerOptionLabel === selectedLabel
          );
          return option?.answerOption;
        };
        const exam =
          rawResult?.cloudPrepTestDTO?.cloudPrepExamPatternDTO?.exam?.[0];

        const questions = exam?.generatedSectionQuestions || [];

        const normalizedQuestions = questions.map((q: any) => ({
          ...q,
          questionText: q.question,
          options: q.options.map((opt: any) => ({
            answerOption: opt.answerValue,
            answerOptionLabel: opt.answerOption,
            isCorrectAnswer: opt.correctAnswer
          }))
        }));

        const normalizedAnswersArray = Object.entries(
          rawResult?.answers || {}
        ).flatMap(([questionId, answers]: any) => {
          if (!Array.isArray(answers)) return [];

          return answers.map((ans: any) => {
            const selectedText = ans.answerOption
              ? qFindSelectedText(
                  normalizedQuestions,
                  questionId,
                  ans.answerOption
                )
              : null;
            return {
              questionId,
              userSelectedOption: selectedText,
              actualCorrectOption: qFindCorrectOption(
                normalizedQuestions,
                questionId
              ),
              isCorrectAnswer: ans.correctAnswer,
              isSkipped: !selectedText
            };
          });
        });

        const paylaload = {
          ...rawResult,
          testDTO: {
            examPatternDTO: {
              rounds: [
                {
                  sections: [
                    {
                      sectionId: 'cloud-prep',
                      sectionSortOrder: 1,
                      isSectionDisplayAvailable: false,
                      totalQuestions: normalizedQuestions?.length,
                      generatedSectionQuestions: normalizedQuestions
                    }
                  ]
                }
              ]
            }
          },
          answers: {
            'cloud-prep': normalizedAnswersArray
          }
        };

        state.resultById = paylaload;
      }
    );
  }
});

export const {
  setIsOnBreak,
  setRoundName,
  setCurrentSectionName,
  setAllRoundsData,
  setAllSectionsData,
  setSubTopicName,
  setCurrentSectionIndex,
  setCurrentQuestionIndex,
  setShowInstructions,
  setInstructions,
  setHasReadRoundInsructions,
  setHasReadFirstSectionInstructions,
  setResetSatrtFullTest,
  setSelectedCompanyName,
  setSelectedCompanyId,
  setCurrentCodeingQuestionAnswer,
  setAllCodeingQuestions,
  setSelectedCodeingLanguage,
  setAllCompaniesData,
  setUserCompanyTestsStatus,
  setInitailStartFullTestState,
  setUserSelectedCompanyTest,
  setResultByIdTestDTO,
  setTestDetailsUnavailable,
  setUserSelectedCompany,
  resetTestSettings,
  setIsSidebarCollapse,
  setTestId,
  setUserTestId,
  setTestName,
  setCount,
  setCurrentTime,
  setOverallResultSlice,
  setQuestionStartTime,
  setStartedTestId,
  setQuestionId,
  setFilters,
  setIsFullTestResultScreen,
  setQid,
  setMinimisePanel,
  setIsTestStarted,
  setFilteredData,
  setResetFilters,
  setIsTestInstructionsOpen,
  setIsResumeTest,
  setExpandedSection,
  setTestSettings,
  setIsResultsScreen,
  setIsDriveTest,
  setNextRound
} = startFullTest.actions;
export default startFullTest.reducer;
